'use client';
import { css, getColorByToken } from '@wanteddev/wds-engine';
import { createResponsiveStyle, addOpacity } from '../../utils/index.mjs';

// src/components/icon-button/style.ts
var getDefaultSize = (variant) => {
  switch (variant) {
    case "outlined":
    case "solid":
      return "normal";
    default:
      return 24;
  }
};
var iconButtonStyle = ({ xs, sm, md, lg, xl, ...props }) => (theme) => css`
    border-radius: 9999px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    ${iconButtonSizeStyle(
  props.size || getDefaultSize(props.variant),
  props.variant
)}
    ${iconButtonColorStyle(props, theme)}

  ${createResponsiveStyle(
  { xs, sm, md, lg, xl },
  theme
)(
  (params = {}) => css`
        ${iconButtonSizeStyle(params.size, props.variant)}
        ${params.sx}
      `
)}
  `;
var iconButtonSizeStyle = (size, variant) => {
  if (typeof size === "number") {
    return css`
      width: ${size}px;
      height: ${size}px;

      ${variant === "background" && css`
        padding: 2px;
      `}

      ${(variant === "solid" || variant === "outlined") && css`
        padding: 6px;
      `}

      svg {
        width: 100% !important;
        height: 100% !important;
      }
    `;
  }
  switch (size) {
    case "normal":
      return css`
        width: 40px;
        height: 40px;

        ${variant === "background" && css`
          padding: 2px;
        `}

        ${(variant === "solid" || variant === "outlined") && css`
          padding: 10px;
        `}

        svg {
          width: 100%;
          height: 100%;
        }
      `;
    case "small":
      return css`
        width: 32px;
        height: 32px;

        ${variant === "background" && css`
          padding: 2px;
        `}

        ${(variant === "solid" || variant === "outlined") && css`
          padding: 7px;
        `}

        svg {
          width: 100%;
          height: 100%;
        }
      `;
  }
};
var iconButtonColorStyle = ({
  variant,
  color,
  interactionColor,
  alternative
}, theme) => {
  switch (variant) {
    case "normal":
      return css`
        background-color: transparent;
        ${Boolean(color) && css`
          color: ${getColorByToken(theme, color)};
        `}
        border: none;
        box-shadow: none;

        ${Boolean(interactionColor) && css`
          & > [wds-component='with-interaction'] {
            background-color: ${getColorByToken(theme, interactionColor)};
          }
        `}

        &:disabled, &[aria-disabled='true'] {
          background-color: transparent;
          color: ${theme.palette.label.disable};
          box-shadow: none;
          border: none;
        }
      `;
    case "background":
      return css`
        border: none;
        box-shadow: none;
        background-color: transparent;
        color: ${alternative ? addOpacity(theme.palette.static.white, theme.opacity[88]) : addOpacity(theme.palette.coolNeutral[50], theme.opacity[61])};

        svg {
          position: relative;

          ${!alternative && css`
            will-change: mix-blend-mode;
            mix-blend-mode: difference;
          `}
        }

        ${Boolean(color) && css`
          color: ${getColorByToken(theme, color)};
        `}

        &::before {
          position: absolute;
          content: '';
          ${alternative ? css`
                background-color: ${addOpacity(
        theme.palette.coolNeutral[30],
        theme.opacity[61]
      )};
              ` : css`
                background-color: ${addOpacity(
        theme.palette.static.white,
        theme.opacity[35]
      )};
                mix-blend-mode: plus-lighter;
                will-change: mix-blend-mode, backdrop-filter;
                backdrop-filter: blur(32px);
              `}
          width: calc(100% + 8px);
          height: calc(100% + 8px);
          top: -4px;
          left: -4px;
          border-radius: inherit;
        }

        &:focus-visible {
          outline: none;

          &::before {
            outline-width: 2px;
            outline-style: solid;
            outline-color: Highlight;
            outline-color: -webkit-focus-ring-color;
          }
        }

        &:disabled,
        &[aria-disabled='true'] {
          background-color: transparent;
          color: ${addOpacity(
        theme.palette.coolNeutral[50],
        theme.opacity[22]
      )};
          border: none;
          box-shadow: none;

          &::before {
            background-color: ${theme.palette.fill.alternative};
            backdrop-filter: none;
            mix-blend-mode: initial;
          }

          svg {
            mix-blend-mode: initial;
          }

          & > [data-role='icon-button-background-alternative'] {
            display: none;
          }
        }
      `;
    case "outlined":
      return css`
        border: none;
        background-color: transparent;
        box-shadow: inset 0 0 0 1px ${theme.palette.line.normal.neutral};
        background-color: transparent;
        ${Boolean(color) && css`
          color: ${getColorByToken(theme, color)};
        `}

        &:disabled, &[aria-disabled='true'] {
          color: ${theme.palette.label.disable};
          background-color: ${theme.palette.background.normal.normal};
          box-shadow: inset 0 0 0 1px ${theme.palette.line.normal.neutral};
        }
      `;
    case "solid":
      return css`
        border: none;
        background-color: ${theme.palette.primary.normal};

        ${Boolean(color) && css`
          color: ${getColorByToken(theme, color)};
        `}

        &:disabled, &[aria-disabled='true'] {
          color: ${theme.palette.label.disable};
          background-color: ${theme.palette.fill.normal};
          backdrop-filter: blur(32px);
        }
      `;
  }
};
var backgroundBlendStyle = (theme) => css`
  position: absolute;
  content: '';
  background-color: ${addOpacity(theme.palette.static.black, theme.opacity[5])};
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  top: -4px;
  left: -4px;
  border-radius: inherit;
`;

export { backgroundBlendStyle, iconButtonStyle };
